import { Command, EditorState } from "prosemirror-state";
import { SelectItem } from "../../Select/Select";
import { FontFamilyKind, FontFamilyMarkAttributes } from "../schema/marks/fontFamily";
import {
	fontFamilyDefinition,
	supportedFontFamilies
} from "client/Misc/fontTools";
import { FontFamily } from "client/Api/common";
import { Mark, Schema } from "prosemirror-model";
import { getUnanimousValueFromSelection } from "../utils";
import { never } from "client_legacy/ndaw/errors";
import { useCallback, useMemo } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { removeMark } from "../commands/removeMark";
import { addMark } from "../commands/addMark";
import { hasMarks } from "../schema";
import { CallbackWithSchemaResult, useExtendWithSchema } from "./useExtendWithSchema";

export type FontFamilyToolHandlers = {
	isSupportedBySchema: true;
	fontFamily: FontFamilyValue | undefined;
	fontFamilyChangeHandler(newValue: FontFamilyValue): void;
	fontFamilyOptions: readonly SelectItem<FontFamilyValue>[];
} | {
	isSupportedBySchema: false;
};

export function useFontFamilyTool(
	editorState: EditorState,
	applyCommand: (command: Command) => void,
	focus?: () => void
): FontFamilyToolHandlers {
	const getValueHandler: CallbackWithSchemaResult<
		[editorState: EditorState],
		FontFamilyValue | undefined
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["fontFamily"]),
		getFontFamilyFromSelection
	);

	const changeHandler: CallbackWithSchemaResult<
		[newValue: FontFamilyValue],
		void
	> = useExtendWithSchema(
		editorState.schema,
		schema => hasMarks(schema, ["fontFamily"]),
		useCallback(
			(
				schema: Schema<never, "fontFamily">,
				newValue: FontFamilyValue
			) => {
				if (newValue.kind === FontFamilyKind.Default) {
					applyCommand(
						removeMark(schema.marks.fontFamily)
					);
					focus?.();
				} else {
					const attrs: FontFamilyMarkAttributes = newValue;

					applyCommand(
						addMark(
							schema.marks.fontFamily
								.create(attrs)
						)
					);
					focus?.();
				}
			},
			[applyCommand, focus]
		)
	);

	return useMemo(
		(): FontFamilyToolHandlers => {
			 if (!getValueHandler.isSupportedBySchema || !changeHandler.isSupportedBySchema) {
				return {
					isSupportedBySchema: false
				};
			 }

			 const value: FontFamilyValue | undefined = getValueHandler(editorState);

			 return {
				isSupportedBySchema: true,
				fontFamily: value,
				fontFamilyChangeHandler: changeHandler,
				fontFamilyOptions: getOptions(value)
			 };
		},
		[
			changeHandler,
			editorState,
			getValueHandler
		]
	);
}

export type FontFamilyValue = {
	kind: FontFamilyKind.Default;
} | {
	kind: FontFamilyKind.Predefined;
	family: FontFamily;
} | {
	kind: FontFamilyKind.Custom;
	familyValue: string;
};

export function getFontFamilyFromSelection(
	schema: Schema<never, "fontFamily">,
	editorState: EditorState
): FontFamilyValue | undefined {
	return getUnanimousValueFromSelection<FontFamilyValue>(
		editorState,
		schema.marks.fontFamily,
		getFontFamilyValueFromMark,
		defaultFontFamilyOption,
		areEqual
	);
}

const defaultFontFamilyOption: FontFamilyValue = {
	kind: FontFamilyKind.Default
};

function createPredefinedValue(fontFamily: FontFamily): FontFamilyValue {
	return {
		kind: FontFamilyKind.Predefined,
		family: fontFamily
	};
};

const predefinedFontFamilyToValueMap: Map<FontFamily, FontFamilyValue> = fromArray(
	supportedFontFamilies,
	family => family,
	family => createPredefinedValue(family)
);

function getFontFamilyValueFromMark(mark: Mark): FontFamilyValue {
	const attrs = mark.attrs as FontFamilyMarkAttributes;
	if (attrs.kind === FontFamilyKind.Custom) {
		return attrs;
	}

	return ensureNotNil(
		predefinedFontFamilyToValueMap.get(attrs.family)
	);
}

function createPredefinedOption(fontFamily: FontFamily): SelectItem<FontFamilyValue> {
	return {
		value: ensureNotNil(predefinedFontFamilyToValueMap.get(fontFamily)),
		text: fontFamilyDefinition[fontFamily].title,
		valueTrackingId: `predefined-${fontFamily}`
	};
};

const fontFamilyPredefinedOptions: readonly SelectItem<FontFamilyValue>[] = [
	{
		value: defaultFontFamilyOption,
		text: `(${localize("_DefaultFont")})`,
		valueTrackingId: "default"
	},
	...supportedFontFamilies.map(f => createPredefinedOption(f))
];

function areEqual(fontFamily1: FontFamilyValue, fontFamily2: FontFamilyValue): boolean {
	switch (fontFamily1.kind) {
		case FontFamilyKind.Default:
			return fontFamily2.kind === FontFamilyKind.Default;
		case FontFamilyKind.Predefined:
			return fontFamily2.kind === FontFamilyKind.Predefined
				&& fontFamily1.family === fontFamily2.family;
		case FontFamilyKind.Custom:
			return fontFamily2.kind === FontFamilyKind.Custom
				&& fontFamily1.familyValue === fontFamily2.familyValue;
		default:
			never(fontFamily1);
	}
}

function getOptions(currentValue: FontFamilyValue | undefined): readonly SelectItem<FontFamilyValue>[] {
	const result: SelectItem<FontFamilyValue>[] = [
		...fontFamilyPredefinedOptions
	];

	if (isNotNil(currentValue) && currentValue.kind === FontFamilyKind.Custom) {
		result.push({
			value: currentValue,
			text: currentValue.familyValue,
			valueTrackingId: `custom-${currentValue.familyValue}`
		});
	}

	return result;
}